import React, { useState, useRef, useEffect } from 'react';
import './Login.css';
import lotusLogo from '../../Logo.png';
import ckLogo from '../../CK-logo.png';
import { useAuth } from '../../contexts/AuthContext';
import { PASSWORD_MIN_LENGTH, PASSWORD_REQUIREMENTS_HINT, validatePassword } from '../../utils/passwordPolicy';

const Login = () => {
  const { login, signup, useFirebase, requestPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const forgotPanelRef = useRef(null);

  useEffect(() => {
    if (forgotOpen && forgotPanelRef.current) {
      forgotPanelRef.current.focus();
    }
  }, [forgotOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (isSignUp) {
      const nameTrim = String(displayName || '').trim();
      if (!nameTrim) {
        setError('Please enter your name.');
        return;
      }
      if (useFirebase) {
        const pwCheck = validatePassword(password);
        if (!pwCheck.ok) {
          setError(pwCheck.message);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      const result = isSignUp
        ? await signup(email, password, displayName)
        : await login(email, password);
      if (!result.success) {
        setError(result.error || 'Something went wrong.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPasswordSubmit = async () => {
    setForgotError('');
    setForgotSuccess(false);
    setForgotSubmitting(true);
    try {
      const result = await requestPasswordReset(email);
      if (result.success) {
        setForgotSuccess(true);
      } else {
        setForgotError(result.error || 'Something went wrong.');
      }
    } catch (err) {
      setForgotError(err.message || 'Something went wrong.');
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-left">
          <div className="login-branding">
            <div className="brand-logo-section">
              <div className="brand-logo-partners">
                <img
                  src={lotusLogo}
                  alt="Lotus STEMM Logo"
                  className="brand-partner-logo"
                />
                <span className="brand-logo-divider">×</span>
                <a
                  href="https://campfirekinship.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brand-partner-logo-link"
                  aria-label="Campfire Kinship (opens in a new tab)"
                >
                  <img
                    src={ckLogo}
                    alt="Campfire Kinship Logo"
                    className="brand-partner-logo"
                  />
                </a>
              </div>
              {/* <span className="brand-logo-subtitle">Breaking Barriers Research Project</span> */}
            </div>
            <h1 className="brand-title">Welcome to Assume We Can: Break Barriers, Build Opportunities</h1>
            <p className="brand-description">
            This learning space brings together insights from Lotus STEMM’s Assume We Can: Break Barriers, Build Opportunities research conducted over 3 years. It shares key findings, stories and resources that highlight the challenges and opportunities experienced by South Asian women in STEMM in Canada. Through these modules, we aim to support learning, reflection and action toward creating more inclusive workplaces. 
            </p>
          </div>
          
          {/* <div className="login-features">
            <div className="feature">
              <div className="feature-icon" aria-hidden="true">📚</div>
              <div className="feature-text">
                <h2>Expert-Led Modules</h2>
                <p>Learn from experienced professionals in your field</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon" aria-hidden="true">🤝</div>
              <div className="feature-text">
                <h2>Community Support</h2>
                <p>Connect with peers and mentors who share your goals</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon" aria-hidden="true">🏆</div>
              <div className="feature-text">
                <h2>Earn Certificates</h2>
                <p>Showcase your achievements with recognized credentials</p>
              </div>
            </div>
          </div> */}
        </div>

        <div className="login-right">
          <div className="login-card">
            <div className="login-header">
              <h2 className="login-title">{isSignUp ? 'Create account' : 'Sign In'}</h2>
              <p className="login-subtitle">
                {isSignUp ? 'Create an account to access the learning platform' : 'Welcome back! Please sign in to continue'}
              </p>
            </div>

            {error && (
              <div className="login-error" role="alert">
                {error}
              </div>
            )}

            {!useFirebase && (
              <p className="login-demo-hint">Demo mode: any email and password will sign you in.</p>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              {isSignUp && (
                <div className="form-group">
                  <label htmlFor="displayName" className="form-label">Your name</label>
                  <input
                    type="text"
                    id="displayName"
                    className="form-input"
                    placeholder="Full name (shown on certificates)"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                    autoComplete="name"
                  />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  className="form-input"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group form-group--password">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  id="password"
                  className="form-input"
                  placeholder={isSignUp ? PASSWORD_REQUIREMENTS_HINT : 'Enter your password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={useFirebase && isSignUp ? PASSWORD_MIN_LENGTH : undefined}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                />
                {isSignUp && useFirebase ? (
                  <p className="login-password-hint">{PASSWORD_REQUIREMENTS_HINT}</p>
                ) : null}
                {!isSignUp && (
                  <div className="form-group-forgot">
                    <button
                      type="button"
                      className="forgot-link forgot-link--button"
                      onClick={() => {
                        setForgotOpen((open) => !open);
                        setForgotError('');
                        setForgotSuccess(false);
                      }}
                      aria-expanded={forgotOpen}
                      aria-controls="forgot-password-panel"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>

              {forgotOpen && !isSignUp && (
                <div
                  id="forgot-password-panel"
                  ref={forgotPanelRef}
                  className="login-forgot-panel"
                  tabIndex={-1}
                >
                  <h3 className="login-forgot-heading">Reset your password</h3>
                  <p className="login-forgot-copy">
                    Use the email address above, then choose &quot;Send reset link&quot;. If an account exists for that
                    address, you will receive an email with a link to choose a new password.
                  </p>
                  {!useFirebase && (
                    <p className="login-demo-hint" role="status">
                      Demo mode: password reset is disabled until Firebase is configured for this deployment.
                    </p>
                  )}
                  {useFirebase && forgotSuccess && (
                    <p className="login-forgot-success" role="status">
                      If an account exists for that email, we sent password reset instructions. Check your inbox and spam
                      folder.
                    </p>
                  )}
                  {useFirebase && !forgotSuccess && forgotError && (
                    <div className="login-error" role="alert">
                      {forgotError}
                    </div>
                  )}
                  {useFirebase && !forgotSuccess && (
                    <button
                      type="button"
                      className="login-btn login-btn--secondary"
                      disabled={forgotSubmitting}
                      onClick={handleForgotPasswordSubmit}
                    >
                      {forgotSubmitting ? 'Sending…' : 'Send reset link'}
                    </button>
                  )}
                </div>
              )}

              <button type="submit" className="login-btn" disabled={submitting}>
                {submitting ? 'Please wait…' : isSignUp ? 'Sign up' : 'Sign In'}
                {!submitting && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                  </svg>
                )}
              </button>
            </form>

            {/* <div className="login-divider">
              <span>or continue with</span>
            </div>

            <div className="social-login">
              <button className="social-btn" aria-label="Sign in with Google">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </button>
              <button className="social-btn" aria-label="Sign in with LinkedIn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
                LinkedIn
              </button>
            </div> */}

            <p className="signup-text">
              {isSignUp ? (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="signup-link-btn"
                    onClick={() => {
                      setIsSignUp(false);
                      setError('');
                      setForgotOpen(false);
                      setForgotError('');
                      setForgotSuccess(false);
                    }}
                  >
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    className="signup-link-btn"
                    onClick={() => {
                      setIsSignUp(true);
                      setError('');
                      setForgotOpen(false);
                      setForgotError('');
                      setForgotSuccess(false);
                    }}
                  >
                    Sign up here
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;

